import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info } from 'lucide-react';

interface BulkOperationsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApplyOperation: (operation: 'percentage' | 'fixed', value: number) => void;
  selectedCount: number;
  totalCount: number;
}

export default function BulkOperationsDialog({
  open,
  onOpenChange,
  onApplyOperation,
  selectedCount,
  totalCount,
}: BulkOperationsDialogProps) {
  const [operation, setOperation] = useState<'percentage' | 'fixed'>('percentage');
  const [percentageValue, setPercentageValue] = useState<string>('');
  const [fixedValue, setFixedValue] = useState<string>('');

  const handleApply = () => {
    if (operation === 'percentage') {
      const value = parseFloat(percentageValue);
      if (!isNaN(value)) {
        onApplyOperation('percentage', value);
        handleClose();
      }
    } else {
      const value = parseFloat(fixedValue);
      if (!isNaN(value) && value >= 0) {
        onApplyOperation('fixed', value);
        handleClose();
      }
    }
  };

  const handleClose = () => {
    setPercentageValue('');
    setFixedValue('');
    setOperation('percentage');
    onOpenChange(false);
  };

  const isValid = operation === 'percentage' 
    ? percentageValue !== '' && !isNaN(parseFloat(percentageValue))
    : fixedValue !== '' && !isNaN(parseFloat(fixedValue)) && parseFloat(fixedValue) >= 0;

  const itemCount = selectedCount > 0 ? selectedCount : totalCount;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[425px]">
        <DialogHeader className="space-y-1">
          <DialogTitle className="text-lg sm:text-xl">Bulk Price Operations</DialogTitle>
          <DialogDescription className="text-sm">
            Apply price changes to {itemCount} item{itemCount !== 1 ? 's' : ''}.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-3 sm:py-4">
          <Alert className="py-2 sm:py-3">
            <Info className="h-4 w-4" />
            <AlertDescription className="text-xs sm:text-sm">
              {selectedCount > 0 
                ? `Changes will be applied to ${selectedCount} selected item${selectedCount !== 1 ? 's' : ''}.`
                : `No items selected. Changes will be applied to all ${totalCount} items.`
              }
            </AlertDescription>
          </Alert>

          <RadioGroup value={operation} onValueChange={(value: 'percentage' | 'fixed') => setOperation(value)}>
            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-start space-x-2">
                <RadioGroupItem value="percentage" id="percentage" className="mt-1" />
                <div className="grid gap-1.5 sm:gap-2 flex-1">
                  <Label htmlFor="percentage" className="text-sm sm:text-base font-medium">
                    Percentage Change
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      placeholder="e.g., 10 for 10% increase"
                      value={percentageValue}
                      onChange={(e) => setPercentageValue(e.target.value)}
                      disabled={operation !== 'percentage'}
                      className="flex-1 text-sm sm:text-base"
                    />
                    <span className="text-sm text-muted-foreground">%</span>
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Use positive values for increase, negative for decrease
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-2">
                <RadioGroupItem value="fixed" id="fixed" className="mt-1" />
                <div className="grid gap-1.5 sm:gap-2 flex-1">
                  <Label htmlFor="fixed" className="text-sm sm:text-base font-medium">
                    Fixed Price
                  </Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">₹</span>
                    <Input
                      type="number"
                      placeholder="e.g., 100"
                      value={fixedValue}
                      onChange={(e) => setFixedValue(e.target.value)}
                      disabled={operation !== 'fixed'}
                      className="flex-1 text-sm sm:text-base"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Set all selected items to this price
                  </p>
                </div>
              </div>
            </div>
          </RadioGroup>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={handleClose} className="w-full sm:w-auto order-2 sm:order-1">
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={!isValid} className="w-full sm:w-auto order-1 sm:order-2">
            Apply Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}